import $ from 'jquery';
import { Component, forwardRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useDrag } from '@use-gesture/react';
import { debounce, isTouchDevice } from '../../GlobalFunctions';
import { updateStorybookPercentageRead } from '../../Session';
import { AppDispatch, RootState } from '../../ApplicationState';
import { ConsoleLogger } from '@aws-amplify/core';

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
  session: state.session,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateStorybookPercentageRead: (percent: number) => dispatch(updateStorybookPercentageRead(percent)),
});
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  url: string;
  pages: number;
  withAudio: boolean | null;
  format?: string;
  currentPage: number;
  changePage: (page: number) => void;
  setPagesDisplayed: (numPages: number) => void;
  background: string;
  isShowBook: boolean;
  setPauseState: (newPause: boolean) => void;
  pauseState: boolean;
  autoPlay: boolean;
  goToNextPage?: () => void; // (typing kept if you had it elsewhere)
  goToPreviousPage?: () => void; // (typing kept if you had it elsewhere)
};
interface State {
  redrawRequests: number;
  readingLevel: string;
  withAudioOffset: number;
}
class Flipbook extends Component<Props, State> {
  assetUrls: string[];
  baseImgUrl: string;
  originalBookPageWidth: number;
  originalBookPageHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  imageWidth: number;
  imageHeight: number;

  // bind once so we can add/remove as listener
  private boundCenterFlipbook = () => this.centerFlipbook();

  constructor(props: Props) {
    super(props);

    this.assetUrls = [];
    this.baseImgUrl = 'https://s3.amazonaws.com/peekaplatform/media30/images/flipbooks/';
    this.generateAssetUrls(this.props.url, this.props.pages);

    this.originalBookPageWidth = 1621;
    this.originalBookPageHeight = 2100;
    this.viewportWidth = 0;
    this.viewportHeight = 0;
    this.imageWidth = 0;
    this.imageHeight = 0;

    this.state = {
      redrawRequests: 0,
      readingLevel: this.props.url[this.props.url.length - 2],
      withAudioOffset: this.props.withAudio ? -50 : 0,
    };

    this.resizeFlipbook = this.resizeFlipbook.bind(this);
    this.updateViewportDimensions = this.updateViewportDimensions.bind(this);
    this.handleOnSwipeLeft = this.handleOnSwipeLeft.bind(this);
    this.handleOnSwipeRight = this.handleOnSwipeRight.bind(this);
    this.updatePageForMixpanel = this.updatePageForMixpanel.bind(this);
  }

  // ---- RTL helper ---------------------------------------------------------
  isRtl(): boolean {
    return this.props.url.startsWith('ar');
  }

  updatePageForMixpanel(page: number) {
    const currentPercentage = Math.ceil((page / this.props.pages) * 100);

    if (!this.props.session.storybookTracker || this.props.session.storybookTracker.percent < currentPercentage) {
      this.props.updateStorybookPercentageRead(currentPercentage);
    }
  }

  updateViewportDimensions() {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const headerHeight = $('#FlipbookHeader').height() as number;

    this.viewportHeight = windowHeight - headerHeight;
    this.viewportWidth = windowWidth;
  }

  generateAssetUrls(url: string, pages: number) {
    const imageFormat = this.props.format ? this.props.format : 'jpg';
    this.assetUrls = [];
    for (let i = 0; i < pages; i++) {
      this.assetUrls.push(`${this.baseImgUrl + url}page${i}.${imageFormat}`);
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.resizeFlipbook);
    // we also want to re-center on resize (especially for RTL)
    $(window).on('resize', this.boundCenterFlipbook);

    if ($('#Flipbook').length) {
      if (!$('#Flipbook').turn('is')) {
        this.initFlipbook();
      }
    }
  }

  UNSAFE_componentWillMount() {
    if (this.state.readingLevel === '3' || this.state.readingLevel === '4') {
      this.originalBookPageWidth = 1500;
      this.originalBookPageHeight = 2180;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeFlipbook);
    $(window).off('resize', this.boundCenterFlipbook);
  }

  componentDidUpdate(prevProps: Props) {
    if ($('#Flipbook').length) {
      if (!$('#Flipbook').turn('is')) {
        this.initFlipbook();
      }

      if (prevProps.currentPage !== this.props.currentPage) {
        this.updateFlipbook(this.props.currentPage);
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.url !== this.props.url) {
      this.generateAssetUrls(nextProps.url, nextProps.pages);
    } else if (nextProps.withAudio !== this.props.withAudio) {
      this.setState({ withAudioOffset: nextProps.withAudio ? -50 : 0 });
    }
  }

  initFlipbook() {
    const rtl = this.isRtl();

    $('#Flipbook').turn({
      autoCenterAbs: false, // disable for RTL; we will center manually
      acceleration: true,
      gradients: false,
      direction: rtl ? 'rtl' : 'ltr',
      page: this.props.currentPage || 1, // keep logical page index; RTL still starts at 1 visually on the right
      when: {
        turned: (event, page: number) => {
          this.props.changePage(page);
          // ensure it's centered after every turn
          this.centerFlipbook();
        },
      },
    });

    // Center once after init (after images/layout sizes settle)
    setTimeout(() => this.centerFlipbook(), 0);

    this.resizeFlipbook();
  }

  private centerFlipbook() {
    const $book = $('#Flipbook');
    const $wrapper = $('#FlipbookWrapper');

    if (!$book.length || !$wrapper.length) return;

    // Make wrapper the positioning context and ensure it reserves space
    $wrapper.css({
      position: 'relative',
      height: `${this.imageHeight}px`,
    });

    // Center the whole book horizontally (direction-agnostic)
    $book.css({
      position: 'absolute',
      left: '50%',
      right: 'auto',
      transform: 'translateX(-50%)',
      margin: 0,
    });

    // Fix individual page anchoring for RTL
    if (this.isRtl()) {
      $book.find('.turn-page').css({ left: 'auto', right: 0 });
    } else {
      $book.find('.turn-page').css({ left: 0, right: 'auto' });
    }
  }

  resizeFlipbook() {
    this.updateViewportDimensions();

    const viewportAspectRatio = this.viewportWidth / this.viewportHeight;
    const doublePageAspectRatio = (this.originalBookPageWidth * 2) / this.originalBookPageHeight;

    let pagesDisplayed: number;
    if (viewportAspectRatio >= doublePageAspectRatio) {
      $('#Flipbook').turn('display', 'double');
      pagesDisplayed = 2;
      this.props.setPagesDisplayed(2);
    } else {
      $('#Flipbook').turn('display', 'single');
      pagesDisplayed = 1;
      this.props.setPagesDisplayed(1);
    }

    const bookPageAspectRatio = (this.originalBookPageWidth * pagesDisplayed) / this.originalBookPageHeight;

    if (viewportAspectRatio >= bookPageAspectRatio) {
      // Height constrained
      this.imageWidth = (bookPageAspectRatio / pagesDisplayed) * this.viewportHeight;
      this.imageHeight = this.viewportHeight + this.state.withAudioOffset;
    } else {
      // Width constrained
      this.imageWidth = this.viewportWidth;
      this.imageHeight = this.viewportWidth / bookPageAspectRatio + this.state.withAudioOffset;
    }

    $('#Flipbook').turn('size', this.imageWidth * pagesDisplayed, this.imageHeight);
    $('#Flipbook').turn('resize');
    this.updateFlipbook(this.props.currentPage);
    this.setState({ redrawRequests: this.state.redrawRequests + 1 }, () => {
      // re-center after a resize/redraw (especially important for RTL)
      this.centerFlipbook();
    });
  }

  halveWidthIfOnFirstPage(pagesDisplayed: number) {
    if (pagesDisplayed === 2 && this.props.currentPage === 1) {
      this.imageWidth = Math.floor(this.imageWidth / 2);
    }
  }

  updateFlipbook(page: number) {
    this.updatePageForMixpanel(page);
    if (!this.props.autoPlay && !this.props.pauseState) {
      this.props.setPauseState(true);
    }
    $('#Flipbook').turn('page', page);
    // make sure position is corrected after programmatic page jumps
    this.centerFlipbook();
  }

  generateFlipbookAssets() {
    const rtl = this.isRtl();
    return (
      <div id='FlipbookWrapper' className={this.props.background} style={{ position: 'relative' }}>
        <div id='Flipbook' style={{ direction: rtl ? 'rtl' : 'ltr', textAlign: rtl ? 'right' : 'left' }}>
          {this.assetUrls.map((result, index) => (
            <div style={{ backgroundColor: 'white' }} key={index}>
              <img alt='Book page' src={result} style={{ width: this.imageWidth, height: this.imageHeight }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    if (!this.props.isShowBook) {
      return null;
    }

    if (isTouchDevice()) {
      return <div {...(this.props as any).gestureProps}>{this.generateFlipbookAssets()}</div>;
    }

    return <div>{this.generateFlipbookAssets()}</div>;
  }

  handleOnSwipeLeft = debounce(
    function () {
      if (!isTouchDevice()) {
        return;
      }

      // If you also want swipe to reverse in RTL, add logic here
      this.props.goToNextPage && this.props.goToNextPage();
    },
    500,
    true,
  );

  handleOnSwipeRight = debounce(
    function () {
      if (!isTouchDevice()) {
        return;
      }

      // If you also want swipe to reverse in RTL, add logic here
      this.props.goToPreviousPage && this.props.goToPreviousPage();
    },
    500,
    true,
  );
}

const FlipbookWithGestures = (props: any) => {
  const bind = useDrag(
    ({ swipe: [swipeX] }) => {
      if (!isTouchDevice()) {
        return;
      }

      if (swipeX === 1) {
        // swipe right - go to previous page
        if (props.goToPreviousPage) {
          props.goToPreviousPage();
        }
      } else if (swipeX === -1) {
        // swipe left - go to next page
        if (props.goToNextPage) {
          props.goToNextPage();
        }
      }
    },
    {
      swipe: { distance: 50, velocity: 0.4 },
    },
  );

  return <Flipbook {...props} gestureProps={bind()} />;
};

export default connector(FlipbookWithGestures);
